import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import { useAuth } from '../context/AuthContext';
import Popup from '../components/Popup';

/* ─── styles ─────────────────────────────────────────────────────────────── */
const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');

  .up-page {
    font-family: 'Inter', system-ui, sans-serif;
    min-height: 100vh;
    background: #0a0f1e;
    padding: 56px 20px 80px;
    color: #e2e8f0;
    position: relative;
    overflow: hidden;
  }

  /* ambient blobs */
  .up-blob {
    position: fixed; border-radius: 50%; filter: blur(90px);
    pointer-events: none; z-index: 0;
  }
  .up-blob-1 { width: 600px; height: 600px; background: rgba(99,102,241,0.09); top: -200px; left: -200px; }
  .up-blob-2 { width: 450px; height: 450px; background: rgba(79,70,229,0.07);  bottom: -150px; right: -150px; }
  .up-blob-3 { width: 350px; height: 350px; background: rgba(236,160,28,0.06); top: 40%; right: -100px; }

  .up-inner {
    position: relative; z-index: 1;
    max-width: 900px; margin: 0 auto;
  }

  /* ── hero heading ── */
  .up-hero { text-align: center; margin-bottom: 56px; }

  .up-eyebrow {
    display: inline-block;
    font-size: 11.5px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.1em;
    color: #818cf8;
    background: rgba(99,102,241,0.1); border: 1px solid rgba(99,102,241,0.2);
    border-radius: 100px; padding: 5px 14px; margin-bottom: 18px;
  }

  .up-h1 {
    font-size: 3rem; font-weight: 900;
    color: #f1f5f9; letter-spacing: -0.04em;
    margin: 0 0 14px; line-height: 1.1;
  }
  .up-h1 span {
    background: linear-gradient(135deg, #818cf8, #6366f1);
    -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text;
  }

  .up-sub {
    font-size: 1.05rem; color: #64748b; margin: 0; max-width: 480px; margin: 0 auto;
  }

  /* ── plans grid ── */
  .up-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 24px;
    margin-bottom: 56px;
    align-items: start;
  }

  /* Plan card */
  .up-card {
    background: rgba(255,255,255,0.03);
    border: 1px solid rgba(99,102,241,0.14);
    border-radius: 24px;
    padding: 36px 32px;
    position: relative;
    transition: border-color 0.25s, transform 0.25s;
    animation: upFadeUp 0.5s ease-out both;
  }
  .up-card:hover { border-color: rgba(99,102,241,0.28); transform: translateY(-4px); }

  /* highlighted (popular) card — Pro */
  .up-card-featured {
    background: rgba(99,102,241,0.06);
    border-color: rgba(99,102,241,0.35);
    box-shadow: 0 0 0 1px rgba(99,102,241,0.2), 0 20px 50px rgba(99,102,241,0.12);
  }
  .up-card-featured:hover { border-color: rgba(99,102,241,0.5); }

  /* Business plan card */
  .up-card-business {
    background: rgba(236,160,28,0.04);
    border-color: rgba(236,160,28,0.3);
    box-shadow: 0 0 0 1px rgba(236,160,28,0.15), 0 20px 50px rgba(236,160,28,0.08);
  }
  .up-card-business:hover { border-color: rgba(236,160,28,0.5); transform: translateY(-4px); }

  .up-badge-business {
    background: linear-gradient(135deg, #f59e0b, #d97706);
    color: #0a0f1e;
    box-shadow: 0 4px 14px rgba(236,160,28,0.45);
    top: -14px; left: 50%; transform: translateX(-50%);
  }

  .up-price-num-business { color: #fbbf24; }

  .up-cta-business {
    width: 100%; padding: 14px;
    background: linear-gradient(135deg, #f59e0b, #d97706);
    color: #0a0f1e; border: none; border-radius: 12px;
    font-size: 15px; font-weight: 800; cursor: pointer; font-family: inherit;
    transition: all 0.2s;
    box-shadow: 0 4px 20px rgba(236,160,28,0.35);
  }
  .up-cta-business:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 8px 32px rgba(236,160,28,0.5); }
  .up-cta-business:disabled { opacity: 0.45; cursor: default; transform: none; box-shadow: none; }

  /* current plan ring */
  .up-card-current { border-color: rgba(34,197,94,0.3); box-shadow: 0 0 0 1px rgba(34,197,94,0.15); }
  .up-card-current:hover { border-color: rgba(34,197,94,0.45); }

  @keyframes upFadeUp {
    from { opacity: 0; transform: translateY(24px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  /* Badges */
  .up-badge {
    display: inline-flex; align-items: center; gap: 5px;
    font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em;
    padding: 4px 12px; border-radius: 100px;
    position: absolute; top: -14px; left: 50%; transform: translateX(-50%);
    white-space: nowrap;
  }
  .up-badge-popular {
    background: linear-gradient(135deg, #6366f1, #4f46e5);
    color: white;
    box-shadow: 0 4px 14px rgba(99,102,241,0.45);
  }
  .up-badge-current {
    background: rgba(34,197,94,0.15);
    border: 1px solid rgba(34,197,94,0.3);
    color: #86efac;
    top: -14px; left: auto; right: 28px; transform: none;
  }

  /* Plan header */
  .up-plan-name {
    font-size: 1.35rem; font-weight: 900; color: #f1f5f9;
    margin: 0 0 4px;
  }
  .up-plan-desc { font-size: 13px; color: #64748b; margin: 0 0 20px; }

  .up-price-row { display: flex; align-items: baseline; gap: 4px; margin-bottom: 28px; }
  .up-price-num  {
    font-size: 3rem; font-weight: 900; color: #f1f5f9; letter-spacing: -0.04em; line-height: 1;
  }
  .up-price-period { font-size: 14px; color: #475569; }

  /* Feature list */
  .up-divider { height: 1px; background: rgba(99,102,241,0.1); margin-bottom: 20px; }

  .up-features { list-style: none; padding: 0; margin: 0 0 28px; display: flex; flex-direction: column; gap: 10px; }
  .up-feature-row { display: flex; align-items: center; gap: 10px; font-size: 14px; }

  .up-check { font-size: 14px; color: #22c55e; flex-shrink: 0; }
  .up-cross { font-size: 14px; color: #334155; flex-shrink: 0; }

  .up-feat-text-on  { color: #94a3b8; }
  .up-feat-text-off { color: #334155; }
  .up-feat-gold { color: #fbbf24; font-weight: 700; }

  /* CTA button */
  .ft-cta-primary {
    display: inline-flex; align-items: center; gap: 8px;
    background: linear-gradient(135deg, #6366f1, #4f46e5); color: white;
    text-decoration: none; padding: 13px 28px; border-radius: 12px;
    font-size: 15px; font-weight: 700;
    box-shadow: 0 4px 20px rgba(99,102,241,0.35); transition: all 0.2s;
  }
  .ft-cta-primary:hover { transform: translateY(-2px); box-shadow: 0 8px 32px rgba(99,102,241,0.5); }

  .ft-cta-secondary {
    display: inline-flex; align-items: center; gap: 8px;
    background: rgba(255,255,255,0.05); color: #94a3b8;
    text-decoration: none; padding: 13px 28px; border-radius: 12px;
    font-size: 15px; font-weight: 700;
    border: 1px solid rgba(99,102,241,0.18); transition: all 0.2s;
  }
  .ft-cta-secondary:hover { background: rgba(99,102,241,0.12); color: #a5b4fc; }

  /* guest card buttons */
  .up-guest-btns { display: flex; flex-direction: column; gap: 10px; }
  .up-feat-link {
    width: 100%; padding: 13px;
    box-sizing: border-box;
    background: rgba(99,102,241,0.08);
    border: 1px solid rgba(99,102,241,0.2);
    color: #a5b4fc; border-radius: 12px;
    font-size: 14px; font-weight: 700;
    text-align: center; text-decoration: none;
    display: block; transition: all 0.2s;
  }
  .up-feat-link:hover { background: rgba(99,102,241,0.18); }

  .up-signup-link {
    width: 100%; padding: 13px;
    box-sizing: border-box;
    background: linear-gradient(135deg, #6366f1, #4f46e5); color: white;
    border-radius: 12px; font-size: 14px; font-weight: 700;
    text-align: center; text-decoration: none;
    display: block;
    box-shadow: 0 4px 16px rgba(99,102,241,0.3);
    transition: all 0.2s;
  }
  .up-signup-link:hover { transform: translateY(-2px); box-shadow: 0 8px 28px rgba(99,102,241,0.5); }

  .up-login-link {
    display: block; text-align: center;
    font-size: 13px; color: #475569;
    text-decoration: none; margin-top: 4px;
    transition: color 0.2s;
  }
  .up-login-link:hover { color: #94a3b8; }
  .up-login-link span { color: #818cf8; }
  .up-cta-primary {
    width: 100%; padding: 14px;
    background: linear-gradient(135deg, #6366f1, #4f46e5);
    color: white; border: none; border-radius: 12px;
    font-size: 15px; font-weight: 700; cursor: pointer; font-family: inherit;
    transition: all 0.2s;
    box-shadow: 0 4px 20px rgba(99,102,241,0.35);
  }
  .up-cta-primary:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 8px 32px rgba(99,102,241,0.5); }
  .up-cta-primary:disabled { opacity: 0.45; cursor: default; transform: none; box-shadow: none; }

  .up-cta-muted {
    width: 100%; padding: 14px;
    background: rgba(255,255,255,0.04);
    border: 1px solid rgba(99,102,241,0.14);
    color: #475569; border-radius: 12px;
    font-size: 15px; font-weight: 600; cursor: default; font-family: inherit;
  }

  /* ── bottom strip ── */
  .up-bottom {
    text-align: center;
    border-top: 1px solid rgba(99,102,241,0.1);
    padding-top: 40px;
  }
  .up-bottom-title { font-size: 1.3rem; font-weight: 800; color: #f1f5f9; margin: 0 0 8px; }
  .up-bottom-sub   { font-size: 14px; color: #475569; margin: 0 0 24px; }

  .up-back-btn {
    display: inline-flex; align-items: center; gap: 7px;
    padding: 11px 24px;
    background: rgba(99,102,241,0.1);
    border: 1px solid rgba(99,102,241,0.2);
    color: #a5b4fc;
    border-radius: 10px; font-size: 14px; font-weight: 700;
    cursor: pointer; font-family: inherit; transition: all 0.2s;
  }
  .up-back-btn:hover { background: rgba(99,102,241,0.18); }

  @media (max-width: 600px) {
    .up-h1 { font-size: 2rem; }
    .up-grid { grid-template-columns: 1fr; }
  }
`;

/* ─── plan data ──────────────────────────────────────────────────────────── */
const PLANS = [
  {
    id: 'free',
    name: 'Free',
    price: '$0',
    period: '/ forever',
    description: 'Perfect for getting started',
    features: [
      { name: 'Up to 20 links', on: true },
      { name: 'Basic URL shortening', on: true },
      { name: 'Click tracking & stats', on: true },
      { name: 'Custom short aliases', on: false },
      { name: 'QR code generation', on: true },
      { name: 'Password protection', on: false },
      { name: 'Link expiration dates', on: false },
      { name: 'Analytics dashboard', on: false },
      { name: 'Brand URL structure', on: false },
      { name: 'Self-Healing Links', on: false },
      { name: 'Link Intelligence Score', on: false },
    ],
    buttonText: 'Downgrade to Free',
  },
  {
    id: 'pro',
    name: 'Pro',
    price: '$19',
    period: '/ month',
    description: 'For individuals and small teams',
    popular: true,
    features: [
      { name: 'Up to 1,000 links', on: true },
      { name: 'Basic URL shortening', on: true },
      { name: 'Click tracking & stats', on: true },
      { name: 'Custom short aliases', on: true },
      { name: 'QR code generation', on: true },
      { name: 'Password protection', on: true },
      { name: 'Link expiration dates', on: true },
      { name: 'Analytics dashboard', on: true },
      { name: '5 Brands Subdomain', on: true },
      { name: '5 Self-Healing Links (Email alerts)', on: true },
      { name: 'Link Intelligence Score', on: false },
    ],
    buttonText: 'Upgrade to Pro →',
  },
  {
    id: 'business',
    name: 'Business',
    price: '$49',
    period: '/ month',
    description: 'For teams & brands at scale',
    business: true,
    features: [
      { name: 'Up to 10,000 links', on: true },
      { name: 'Basic URL shortening', on: true },
      { name: 'Click tracking & stats', on: true },
      { name: 'Custom short aliases', on: true },
      { name: 'QR code generation', on: true },
      { name: 'Password protection', on: true },
      { name: 'Link expiration dates', on: true },
      { name: 'Advanced Analytics', on: true, gold: true },
      { name: '100 Brands Subdomain', on: true, gold: true },
      { name: '50 Self-Healing Links (Email alerts)', on: true, gold: true },
      { name: 'Link Intelligence Score', on: true, gold: true },


    ],
    buttonText: 'Upgrade to Business →',
  },
];

/* ─── component ──────────────────────────────────────────────────────────── */
const Upgrade = () => {
  const { user, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  // Popup state
  const [popup, setPopup] = useState({ isOpen: false, title: '', message: '', type: 'confirm', onConfirm: null });

  const planTier = { free: 0, pro: 1, business: 2 };
  const isLower = (target, current) => (planTier[target] ?? 0) < (planTier[current] ?? 0);

  const getButtonLabel = (planId) => {
    if (user?.plan === planId) return 'Current Plan';
    if (isLower(planId, user?.plan)) return PLANS.find(p => p.id === planId)?.name + ' (lower tier)';
    return PLANS.find(p => p.id === planId)?.buttonText || 'Select';
  };

  const handleUpgrade = async (planId) => {
    if (!user) return;
    if (user.plan === planId) {
      setPopup({ isOpen: true, title: 'Already Subscribed', message: 'You are already on this plan!', type: 'info' });
      return;
    }
    if (isLower(planId, user.plan)) {
      setPopup({ isOpen: true, title: 'Downgrade Restriction', message: 'You cannot downgrade to a lower tier plan. Contact support if needed.', type: 'info' });
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${API_BASE_URL}/api/subscription/create-checkout`, { plan: planId });
      // Redirect to Stripe Checkout
      window.location.href = res.data.url;
    } catch {
      setPopup({ isOpen: true, title: 'Error', message: 'Failed to start checkout. Please try again.', type: 'danger' });
      setLoading(false);
    }
  };

  const handleManageSubscription = async () => {
    try {
      const res = await axios.post(`${API_BASE_URL}/api/subscription/portal`);
      window.location.href = res.data.url;
    } catch {
      setPopup({ isOpen: true, title: 'Error', message: 'Failed to open billing portal.', type: 'danger' });
    }
  };

  return (
    <div className="up-page">
      <style>{STYLES}</style>

      <div className="up-blob up-blob-1" />
      <div className="up-blob up-blob-2" />
      <div className="up-blob up-blob-3" />

      <div className="up-inner">

        {/* ── Hero heading ── */}
        <div className="up-hero">
          <div className="up-eyebrow">✦ Pricing Plans</div>
          <h1 className="up-h1">Choose the plan that<br /><span>fits your needs</span></h1>
          <p className="up-sub">Unlock powerful features to supercharge your link management. Cancel anytime.</p>
        </div>

        {/* ── Plan cards ── */}
        <div className="up-grid">
          {PLANS.map((plan, i) => {
            const isGuest = !user;
            const isCurrent = user?.plan === plan.id;
            const isDowngrade = !isCurrent && isLower(plan.id, user?.plan);
            const disabled = isCurrent || isDowngrade || loading;

            const cardClass = ['up-card',
              plan.business && !isCurrent ? 'up-card-business' : '',
              plan.popular && !isCurrent ? 'up-card-featured' : '',
              isCurrent ? 'up-card-current' : '',
            ].filter(Boolean).join(' ');

            return (
              <div key={plan.id} className={cardClass} style={{ animationDelay: `${i * 0.1}s` }}>

                {/* Badges */}
                {!isGuest && plan.popular && !isCurrent && (
                  <div className="up-badge up-badge-popular">⭐ Most Popular</div>
                )}
                {!isGuest && plan.business && !isCurrent && (
                  <div className="up-badge up-badge-business">🏢 For Teams</div>
                )}
                {!isGuest && isCurrent && (
                  <div className="up-badge up-badge-current">✓ Current Plan</div>
                )}
                {isGuest && plan.popular && (
                  <div className="up-badge up-badge-popular">⭐ Most Popular</div>
                )}
                {isGuest && plan.business && (
                  <div className="up-badge up-badge-business">🏢 For Teams</div>
                )}

                {/* Header */}
                <p className="up-plan-name">{plan.name}</p>
                <p className="up-plan-desc">{plan.description}</p>

                <div className="up-price-row">
                  <span className={`up-price-num${plan.business ? ' up-price-num-business' : ''}`}>{plan.price}</span>
                  <span className="up-price-period">{plan.period}</span>
                </div>

                <div className="up-divider" />

                {/* Features */}
                <ul className="up-features">
                  {plan.features.map((f, fi) => (
                    <li key={fi} className="up-feature-row">
                      {f.on
                        ? <span className="up-check">✓</span>
                        : <span className="up-cross">✗</span>
                      }
                      <span className={f.gold ? 'up-feat-gold' : (f.on ? 'up-feat-text-on' : 'up-feat-text-off')}>{f.name}</span>
                    </li>
                  ))}
                </ul>

                {/* CTA section — always a flex column so all buttons are full-width & centred */}
                <div className="up-guest-btns">
                  <Link to="/features" className="up-feat-link">📋 Explore Features</Link>
                  {isGuest ? (
                    <>
                      <Link to="/signup" className="up-signup-link">🚀 Get Started Free</Link>
                      <Link to="/login" className="up-login-link">Already have an account? <span>Sign in</span></Link>
                    </>
                  ) : isCurrent || isDowngrade ? (
                    <button className="up-cta-muted" disabled>{getButtonLabel(plan.id)}</button>
                  ) : (
                    <button
                      className={plan.business ? 'up-cta-business' : 'up-cta-primary'}
                      onClick={() => handleUpgrade(plan.id)}
                      disabled={disabled}
                    >
                      {loading ? '⏳ Processing…' : getButtonLabel(plan.id)}
                    </button>
                  )}
                </div>

              </div>
            );
          })}
        </div>

        {/* ── Bottom strip ── */}
        <div className="up-bottom">
          <p className="up-bottom-title">Ready to get started?</p>
          <p className="up-bottom-sub">Join thousands of users who trust RDRTO for their link management needs.</p>
          <button className="up-back-btn" onClick={() => navigate('/dashboard')}>← Back to Dashboard</button>
        </div>

      </div>

      <Popup
        isOpen={popup.isOpen}
        title={popup.title}
        message={popup.message}
        type={popup.type}
        onConfirm={popup.onConfirm || (() => setPopup(p => ({ ...p, isOpen: false })))}
        onCancel={() => setPopup(p => ({ ...p, isOpen: false }))}
      />
    </div>
  );
};

export default Upgrade;
